import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Badge, Spinner } from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import {
  FaArrowLeft,
  FaSync,
  FaFileExcel,
  FaRedo,
  FaSave,
  FaExclamationTriangle,
} from 'react-icons/fa';
import { MdErrorOutline } from 'react-icons/md';
import * as XLSX from 'xlsx';
import { turnoApi } from '../../../services/api/turnoApi';
import { useComedores } from '../../../services/hooks/useComedores';
import { useTipoServiciosAlimentacion } from '../../../services/hooks/useTipoServiciosAlimentacion';
import 'react-toastify/dist/ReactToastify.css';

const formatFecha = (fecha) => {
  if (!fecha) return '—';
  const parte = String(fecha).split('T')[0];
  const [y, m, d] = parte.split('-');
  if (!y || !m || !d) return fecha;
  return `${d}/${m}/${y}`;
};

const GestionErroresCargaMasiva = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem('token');
  const currentUser = JSON.parse(localStorage.getItem('user') || 'null');
  const selectedProject = JSON.parse(localStorage.getItem('selectedProject') || 'null');

  const { comedores, fetchComedoresByProyecto } = useComedores();
  const { tipoServiciosAlimentacion, fetchTipoServiciosAlimentacion } =
    useTipoServiciosAlimentacion();

  const turnoInicial = location.state?.idTurnoCabecera || '';

  const [filtros, setFiltros] = useState({
    id_comedor: '',
    id_tipo_servicio: '',
    id_turno_cabecera: turnoInicial ? String(turnoInicial) : '',
    fecha_desde: '',
    fecha_hasta: '',
    regularizado: '0',
    numero_documento: '',
  });

  useEffect(() => {
    if (location.state?.idTurnoCabecera) {
      setFiltros((prev) => ({
        ...prev,
        id_turno_cabecera: String(location.state.idTurnoCabecera),
        regularizado: '0',
      }));
      setPage(1);
    }
  }, [location.state?.idTurnoCabecera]);
  const [errores, setErrores] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [size] = useState(50);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [documentosEdit, setDocumentosEdit] = useState({});
  const [guardandoId, setGuardandoId] = useState(null);
  const [reintentandoId, setReintentandoId] = useState(null);
  const [reintentandoMasivo, setReintentandoMasivo] = useState(false);

  useEffect(() => {
    if (!token || !currentUser) {
      navigate('/');
    }
  }, [navigate, token, currentUser]);

  useEffect(() => {
    if (selectedProject?.id) {
      fetchComedoresByProyecto(selectedProject.id);
      fetchTipoServiciosAlimentacion();
    }
  }, [
    selectedProject?.id,
    fetchComedoresByProyecto,
    fetchTipoServiciosAlimentacion,
  ]);

  const buildQueryParams = useCallback(() => {
    const params = {
      id_proyecto: selectedProject?.id,
      page,
      size,
    };
    if (filtros.id_comedor) params.id_comedor = filtros.id_comedor;
    if (filtros.id_tipo_servicio) params.id_tipo_servicio = filtros.id_tipo_servicio;
    if (filtros.id_turno_cabecera) params.id_turno_cabecera = filtros.id_turno_cabecera;
    if (filtros.fecha_desde) params.fecha_desde = filtros.fecha_desde;
    if (filtros.fecha_hasta) params.fecha_hasta = filtros.fecha_hasta;
    if (filtros.numero_documento) params.numero_documento = filtros.numero_documento;
    if (filtros.regularizado !== '') {
      params.regularizado = filtros.regularizado === '1';
    }
    return params;
  }, [selectedProject?.id, page, size, filtros]);

  const cargarErrores = useCallback(async () => {
    if (!selectedProject?.id) return;
    setLoading(true);
    try {
      const response = await turnoApi.listErroresCargaMasiva(buildQueryParams());
      const data = response.data?.data || [];
      setErrores(data);
      setTotal(response.data?.total || 0);
      const docs = {};
      data.forEach((err) => {
        docs[err.id] = err.numero_documento || '';
      });
      setDocumentosEdit(docs);
      setSelectedIds([]);
    } catch (error) {
      console.error('Error al cargar errores:', error);
      toast.error('No se pudieron cargar los errores de carga masiva');
      setErrores([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [selectedProject?.id, buildQueryParams]);

  useEffect(() => {
    if (selectedProject?.id) {
      cargarErrores();
    }
  }, [selectedProject?.id, cargarErrores]);

  const pendientesSeleccionables = useMemo(
    () => errores.filter((e) => !e.regularizado && e.es_reintentable),
    [errores]
  );

  const handleFiltroChange = (e) => {
    const { name, value } = e.target;
    setFiltros((prev) => ({ ...prev, [name]: value }));
    setPage(1);
  };

  const handleBuscar = (e) => {
    e.preventDefault();
    setPage(1);
    cargarErrores();
  };

  const toggleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(pendientesSeleccionables.map((e) => e.id));
    } else {
      setSelectedIds([]);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const guardarDocumento = async (errorId) => {
    const doc = (documentosEdit[errorId] || '').trim();
    if (!doc) {
      toast.warning('Ingrese un número de documento válido');
      return;
    }
    setGuardandoId(errorId);
    try {
      await turnoApi.updateDocumentoErrorCargaMasiva(errorId, doc);
      toast.success('Documento actualizado');
      await cargarErrores();
    } catch (error) {
      const msg = error.response?.data?.detail || 'No se pudo guardar el documento';
      toast.error(typeof msg === 'string' ? msg : 'Error al guardar');
    } finally {
      setGuardandoId(null);
    }
  };

  const reintentarUno = async (errorId) => {
    setReintentandoId(errorId);
    try {
      const doc = (documentosEdit[errorId] || '').trim();
      const errActual = errores.find((e) => e.id === errorId);
      if (errActual && doc !== (errActual.numero_documento || '')) {
        await turnoApi.updateDocumentoErrorCargaMasiva(errorId, doc);
      }
      const response = await turnoApi.reintentarErrorCargaMasiva(errorId);
      const data = response.data?.data;
      if (data?.success) {
        toast.success(data.message || 'Registro insertado correctamente');
      } else {
        toast.error(data?.message || 'El reintento falló');
      }
      await cargarErrores();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Error al reintentar inserción';
      toast.error(typeof msg === 'string' ? msg : 'Error al reintentar');
    } finally {
      setReintentandoId(null);
    }
  };

  const reintentarSeleccionados = async () => {
    if (!selectedIds.length) {
      toast.info('Seleccione al menos un error reintentable');
      return;
    }
    setReintentandoMasivo(true);
    try {
      for (const errorId of selectedIds) {
        const doc = (documentosEdit[errorId] || '').trim();
        const errActual = errores.find((e) => e.id === errorId);
        if (errActual && doc !== (errActual.numero_documento || '')) {
          await turnoApi.updateDocumentoErrorCargaMasiva(errorId, doc);
        }
      }
      const response = await turnoApi.reintentarErroresCargaMasivaMasivo(selectedIds);
      const { exitos, fallos } = response.data || {};
      toast.success(`Reintento masivo: ${exitos ?? 0} éxito(s), ${fallos ?? 0} fallo(s)`);
      await cargarErrores();
    } catch (error) {
      toast.error('Error en reintento masivo');
    } finally {
      setReintentandoMasivo(false);
    }
  };

  const exportarExcel = () => {
    if (!errores.length) {
      toast.info('No hay datos para exportar');
      return;
    }
    const filas = errores.map((err) => ({
      Origen: err.origen === 'offline' ? 'Offline' : 'Excel',
      'Fila Excel': err.fila_excel ?? '',
      Documento: err.numero_documento || '',
      Nombres: err.nombres || '',
      Apellidos: err.apellidos || '',
      Comedor: err.comedor_nombre || '',
      Servicio: err.tipo_servicio_descripcion || '',
      'Fecha turno': formatFecha(err.fecha_turno),
      Errores: Array.isArray(err.errores) ? err.errores.join(' | ') : '',
      Regularizado: err.regularizado ? 'Sí' : 'No',
      Reintentable: err.es_reintentable ? 'Sí' : 'No',
      'Fecha registro': err.created_at
        ? new Date(err.created_at).toLocaleString('es-PE')
        : '',
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(filas), 'Errores');
    XLSX.writeFile(wb, `Errores_Carga_Masiva_${Date.now()}.xlsx`);
    toast.success(`Excel exportado (${filas.length} filas)`);
  };

  const totalPaginas = Math.max(1, Math.ceil(total / size));

  return (
    <>
      <style>
        {`
          .dh-reporte-bg {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            min-height: 100vh;
          }
          .dh-main-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 2rem 0;
            margin-bottom: 2rem;
            border-radius: 0 0 30px 30px;
            box-shadow: 0 10px 30px rgba(102, 126, 234, 0.2);
          }
          .dh-main-title { font-size: 2rem; font-weight: 700; margin: 0; }
          .dh-main-subtitle { font-size: 1rem; opacity: 0.9; margin: 0.5rem 0 0 0; }
          .dh-card {
            border: none;
            border-radius: 20px;
            box-shadow: 0 15px 35px rgba(0,0,0,0.1);
            background: white;
          }
          .dh-card-header {
            border-radius: 20px 20px 0 0;
            padding: 1.25rem 1.5rem;
            border: none;
            font-weight: 700;
          }
          .dh-card-header-primary {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
          }
          .dh-card-header-info {
            background: linear-gradient(135deg, #00c6ff 0%, #0072ff 100%);
            color: white;
          }
          .btn-dh-primary {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 10px 18px;
            font-weight: 600;
          }
          .btn-dh-primary:hover { color: white; opacity: 0.95; }
          .btn-dh-secondary {
            background: linear-gradient(135deg, #6b7280 0%, #374151 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 10px 18px;
            font-weight: 600;
          }
          .btn-dh-secondary:hover { color: white; }
          .dh-form-label { font-weight: 600; color: #374151; font-size: 0.9rem; }
          .dh-form-control {
            border-radius: 12px;
            border: 2px solid #e5e7eb;
            padding: 0.5rem 0.75rem;
          }
          .dh-form-control:focus {
            border-color: #667eea;
            box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.2);
          }
          .dh-alert-info {
            background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
            border: 2px solid #3b82f6;
            border-radius: 12px;
            padding: 0.75rem 1rem;
            color: #1e40af;
          }
          .table-errores { font-size: 0.85rem; }
          .table-errores th { white-space: nowrap; }
          .doc-input { max-width: 140px; font-family: monospace; }
        `}
      </style>

      <div className="dh-reporte-bg">
        <div className="dh-main-header">
          <Container>
            <div className="d-flex align-items-center gap-3">
              <MdErrorOutline size={36} />
              <div>
                <h1 className="dh-main-title">Gestión de errores de carga masiva</h1>
                <p className="dh-main-subtitle">
                  Corrija documentos de identidad y reintente la inserción en el turno
                </p>
              </div>
            </div>
          </Container>
        </div>

        <Container className="pb-5">
          <div className="mb-3 d-flex flex-wrap gap-2">
            <Button
              className="btn-dh-secondary d-flex align-items-center gap-2"
              onClick={() => navigate('/procesos/alimentacion/gestionar-turnos')}
            >
              <FaArrowLeft /> Volver a turnos
            </Button>
            <Button
              className="btn-dh-secondary d-flex align-items-center gap-2"
              onClick={cargarErrores}
              disabled={loading}
            >
              <FaSync className={loading ? 'fa-spin' : ''} /> Actualizar
            </Button>
            <Button
              className="btn-dh-secondary d-flex align-items-center gap-2"
              onClick={exportarExcel}
              disabled={!errores.length}
            >
              <FaFileExcel /> Exportar Excel
            </Button>
            <Button
              className="btn-dh-primary d-flex align-items-center gap-2"
              onClick={reintentarSeleccionados}
              disabled={!selectedIds.length || reintentandoMasivo}
            >
              {reintentandoMasivo ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <FaRedo />
              )}
              Reintentar seleccionados ({selectedIds.length})
            </Button>
          </div>

          <Card className="dh-card mb-4">
            <Card.Header className="dh-card-header dh-card-header-info">
              Filtros de búsqueda
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleBuscar}>
                <Row className="g-3">
                  <Col md={3}>
                    <Form.Label className="dh-form-label">Comedor</Form.Label>
                    <Form.Select
                      className="dh-form-control"
                      name="id_comedor"
                      value={filtros.id_comedor}
                      onChange={handleFiltroChange}
                    >
                      <option value="">Todos</option>
                      {comedores.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.nombre}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={3}>
                    <Form.Label className="dh-form-label">Tipo de servicio</Form.Label>
                    <Form.Select
                      className="dh-form-control"
                      name="id_tipo_servicio"
                      value={filtros.id_tipo_servicio}
                      onChange={handleFiltroChange}
                    >
                      <option value="">Todos</option>
                      {tipoServiciosAlimentacion.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.descripcion}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={2}>
                    <Form.Label className="dh-form-label">Fecha desde</Form.Label>
                    <Form.Control
                      type="date"
                      className="dh-form-control"
                      name="fecha_desde"
                      value={filtros.fecha_desde}
                      onChange={handleFiltroChange}
                    />
                  </Col>
                  <Col md={2}>
                    <Form.Label className="dh-form-label">Fecha hasta</Form.Label>
                    <Form.Control
                      type="date"
                      className="dh-form-control"
                      name="fecha_hasta"
                      value={filtros.fecha_hasta}
                      onChange={handleFiltroChange}
                    />
                  </Col>
                  <Col md={2}>
                    <Form.Label className="dh-form-label">Estado</Form.Label>
                    <Form.Select
                      className="dh-form-control"
                      name="regularizado"
                      value={filtros.regularizado}
                      onChange={handleFiltroChange}
                    >
                      <option value="0">Pendientes</option>
                      <option value="1">Regularizados</option>
                      <option value="">Todos</option>
                    </Form.Select>
                  </Col>
                  <Col md={3}>
                    <Form.Label className="dh-form-label">ID turno</Form.Label>
                    <Form.Control
                      type="number"
                      className="dh-form-control"
                      name="id_turno_cabecera"
                      placeholder="Opcional"
                      value={filtros.id_turno_cabecera}
                      onChange={handleFiltroChange}
                    />
                  </Col>
                  <Col md={3}>
                    <Form.Label className="dh-form-label">Buscar documento</Form.Label>
                    <Form.Control
                      type="text"
                      className="dh-form-control"
                      name="numero_documento"
                      placeholder="DNI / documento"
                      value={filtros.numero_documento}
                      onChange={handleFiltroChange}
                    />
                  </Col>
                  <Col md={3} className="d-flex align-items-end">
                    <Button type="submit" className="btn-dh-primary w-100" disabled={loading}>
                      {loading ? <Spinner animation="border" size="sm" /> : 'Buscar'}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>

          <div className="dh-alert dh-alert-info mb-3">
            <FaExclamationTriangle className="me-2" />
            <strong>{total}</strong> registro(s) encontrado(s).
            Solo los errores marcados como reintentables permiten corregir el documento y volver a insertar.
            Los DNI numéricos con menos de 8 dígitos se completan automáticamente con ceros a la izquierda.
          </div>

          <Card className="dh-card">
            <Card.Header className="dh-card-header dh-card-header-primary d-flex justify-content-between align-items-center">
              <span>Errores de carga masiva</span>
              <Badge bg="light" text="dark">
                Página {page} de {totalPaginas}
              </Badge>
            </Card.Header>
            <Card.Body className="p-0">
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-2 text-muted">Cargando errores...</p>
                </div>
              ) : errores.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  No hay errores con los filtros seleccionados.
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover table-errores mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: 40 }}>
                          <input
                            type="checkbox"
                            checked={
                              pendientesSeleccionables.length > 0 &&
                              selectedIds.length === pendientesSeleccionables.length
                            }
                            onChange={(e) => toggleSelectAll(e.target.checked)}
                            disabled={!pendientesSeleccionables.length}
                          />
                        </th>
                        <th>Origen</th>
                        <th>Fila</th>
                        <th>Documento</th>
                        <th>Referencia</th>
                        <th>Comedor</th>
                        <th>Servicio</th>
                        <th>Fecha turno</th>
                        <th>Errores</th>
                        <th>Estado</th>
                        <th style={{ minWidth: 180 }}>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {errores.map((err) => {
                        const pendiente = !err.regularizado;
                        const editable = pendiente && err.es_reintentable;
                        return (
                          <tr key={err.id}>
                            <td>
                              {editable ? (
                                <input
                                  type="checkbox"
                                  checked={selectedIds.includes(err.id)}
                                  onChange={() => toggleSelect(err.id)}
                                />
                              ) : null}
                            </td>
                            <td>
                              {err.origen === 'offline' ? (
                                <Badge bg="info">Offline</Badge>
                              ) : (
                                <Badge bg="light" text="dark">
                                  Excel
                                </Badge>
                              )}
                            </td>
                            <td>{err.fila_excel ?? '—'}</td>
                            <td>
                              {editable ? (
                                <Form.Control
                                  size="sm"
                                  className="dh-form-control doc-input"
                                  value={documentosEdit[err.id] ?? ''}
                                  onChange={(e) =>
                                    setDocumentosEdit((prev) => ({
                                      ...prev,
                                      [err.id]: e.target.value,
                                    }))
                                  }
                                />
                              ) : (
                                <span style={{ fontFamily: 'monospace' }}>
                                  {err.numero_documento || '—'}
                                </span>
                              )}
                            </td>
                            <td>
                              {[err.nombres, err.apellidos].filter(Boolean).join(' ') || '—'}
                            </td>
                            <td>{err.comedor_nombre || '—'}</td>
                            <td>{err.tipo_servicio_descripcion || '—'}</td>
                            <td>{formatFecha(err.fecha_turno)}</td>
                            <td>
                              <ul className="mb-0 ps-3">
                                {(err.errores || []).map((msg, i) => (
                                  <li key={i}>{msg}</li>
                                ))}
                              </ul>
                            </td>
                            <td>
                              {err.regularizado ? (
                                <Badge bg="success">Regularizado</Badge>
                              ) : err.es_reintentable ? (
                                <Badge bg="warning" text="dark">
                                  Pendiente
                                </Badge>
                              ) : (
                                <Badge bg="secondary">No reintentable</Badge>
                              )}
                            </td>
                            <td>
                              {editable ? (
                                <div className="d-flex gap-1 flex-wrap">
                                  <Button
                                    size="sm"
                                    className="btn-dh-secondary"
                                    disabled={guardandoId === err.id}
                                    onClick={() => guardarDocumento(err.id)}
                                    title="Guardar documento"
                                  >
                                    {guardandoId === err.id ? (
                                      <Spinner animation="border" size="sm" />
                                    ) : (
                                      <FaSave />
                                    )}
                                  </Button>
                                  <Button
                                    size="sm"
                                    className="btn-dh-primary"
                                    disabled={reintentandoId === err.id}
                                    onClick={() => reintentarUno(err.id)}
                                    title="Reintentar inserción"
                                  >
                                    {reintentandoId === err.id ? (
                                      <Spinner animation="border" size="sm" />
                                    ) : (
                                      <FaRedo />
                                    )}
                                  </Button>
                                </div>
                              ) : (
                                <span className="text-muted small">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card.Body>
            {totalPaginas > 1 && (
              <Card.Footer className="d-flex justify-content-between align-items-center">
                <Button
                  className="btn-dh-secondary btn-sm"
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Anterior
                </Button>
                <span className="text-muted small">
                  {total} registro(s) — página {page}/{totalPaginas}
                </span>
                <Button
                  className="btn-dh-secondary btn-sm"
                  disabled={page >= totalPaginas || loading}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Siguiente
                </Button>
              </Card.Footer>
            )}
          </Card>
        </Container>
      </div>

      <ToastContainer position="bottom-right" autoClose={3500} />
    </>
  );
};

export default GestionErroresCargaMasiva;
